import { IonApp, IonRouterOutlet, setupIonicReact } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { Route, Redirect, Switch } from 'react-router-dom';
import { SignedIn, SignedOut, RedirectToSignIn } from '@clerk/clerk-react';
import { I18nProvider } from './i18n/I18nProvider';
import DeepLinkRunner from './components/DeepLinkRunner';

import HomePage from './pages/HomePage';
import SignInPage from './pages/SignInPage';
import SignUpPage from './pages/SignUpPage';
import DashboardPage from './pages/DashboardPage';
import ProfilePage from './pages/ProfilePage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import ActivityLogsPage from './pages/ActivityLogsPage';
import TopicsPage from './pages/TopicsPage';
import ChatsPage from './pages/ChatsPage';
import ConversationsPage from './pages/ConversationsPage';
import ChatPage from './pages/ChatPage';
import ContactsPage from './pages/ContactsPage';
import PrivacyDataPage from './pages/PrivacyDataPage';
import QuickDashboardPage from './pages/QuickDashboardPage';
import JoinLandingPage from './pages/JoinLandingPage';
import ReferralLandingPage from './pages/ReferralLandingPage';
import DeepLinkLandingPage from './pages/DeepLinkLandingPage';
import UpgradePage from './pages/UpgradePage';
import OnboardingPage from './pages/OnboardingPage';
import VersionCompatRunner from './components/VersionCompatRunner';
import NotificationNavRunner from './components/NotificationNavRunner';

import { useLoggerInit } from './hooks/useLoggerInit';
import { hasCompletedOnboarding } from './lib/onboarding';

setupIonicReact({
  mode: 'ios',
});

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <>
      <SignedIn>{children}</SignedIn>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </>
  );
};

const App: React.FC = () => {
  useLoggerInit();

  return (
    <IonApp>
      <I18nProvider>
        <IonReactRouter>
          <VersionCompatRunner />
          <NotificationNavRunner />
          <SignedIn>
            <DeepLinkRunner />
          </SignedIn>
          <IonRouterOutlet>
            <Switch>
              <Route
                exact
                path="/"
                render={() =>
                  hasCompletedOnboarding() ? (
                    <HomePage />
                  ) : (
                    <Redirect to="/onboarding" />
                  )
                }
              />
              <Route
                exact
                path="/onboarding"
                render={() =>
                  hasCompletedOnboarding() ? (
                    <Redirect to="/" />
                  ) : (
                    <OnboardingPage />
                  )
                }
              />
              <Route exact path="/terms" component={TermsPage} />
              <Route exact path="/privacy" component={PrivacyPage} />
              <Route exact path="/upgrade" component={UpgradePage} />

              <Route path="/sign-in" component={SignInPage} />
              <Route path="/sign-up" component={SignUpPage} />

              <Route exact path="/quick" component={QuickDashboardPage} />
              <Route exact path="/join" component={JoinLandingPage} />
              <Route exact path="/r/:code" component={ReferralLandingPage} />
              <Route exact path="/c/:id" component={DeepLinkLandingPage} />
              <Route exact path="/t/:id" component={DeepLinkLandingPage} />

              <Route exact path="/dashboard">
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              </Route>
              <Route exact path="/topics">
                <ProtectedRoute>
                  <TopicsPage />
                </ProtectedRoute>
              </Route>
              <Route exact path="/chats">
                <ProtectedRoute>
                  <ChatsPage />
                </ProtectedRoute>
              </Route>
              <Route
                exact
                path="/topics/:topicId/conversations"
                render={(props) => (
                  <ProtectedRoute>
                    <ConversationsPage {...props} />
                  </ProtectedRoute>
                )}
              />
              <Route
                exact
                path="/groups/:topicId/conversations"
                render={({ match, location }) => (
                  <Redirect
                    to={`/topics/${match.params.topicId}/conversations${location.search}`}
                  />
                )}
              />
              <Route exact path="/groups">
                <Redirect to="/topics" />
              </Route>
              <Route
                exact
                path="/chat/:conversationId"
                render={(props) => (
                  <ProtectedRoute>
                    <ChatPage {...props} />
                  </ProtectedRoute>
                )}
              />
              <Route path="/profile">
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              </Route>
              <Route exact path="/activity">
                <ProtectedRoute>
                  <ActivityLogsPage />
                </ProtectedRoute>
              </Route>
              <Route exact path="/contacts">
                <ProtectedRoute>
                  <ContactsPage />
                </ProtectedRoute>
              </Route>
              <Route exact path="/settings/data">
                <ProtectedRoute>
                  <PrivacyDataPage />
                </ProtectedRoute>
              </Route>

              <Route>
                <Redirect to="/" />
              </Route>
            </Switch>
          </IonRouterOutlet>
        </IonReactRouter>
      </I18nProvider>
    </IonApp>
  );
};

export default App;
